import { Typography } from "@material-ui/core"
import { createColumnHelper } from "@tanstack/react-table"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useState } from "react"
import { useInfiniteQuery } from "react-query"
import { certificationAnalytics } from "~/actions/certifications"
import AnalyticsTable from "~/components/Analytics/AnalyticsTable"
import BasicSortHeader from "~/components/Analytics/BasicSortHeader"
import CertificationContext from "~/contexts/Certification"
import usePermissionsRedirect from "~/hooks/usePermissionsRedirect"
import Page from "~/pages/Page"
import Router from "~/router"

import Box from "../../../../../../components/Common/Box"
import useStyles from "./styles"

const columnHelper = createColumnHelper()

const columns = [
  columnHelper.accessor((row) => row.user_id, {
    user_id: "user_id",
    header: ({ column }) => <BasicSortHeader column={column} label="User ID" />,
    id: "user_id",
  }),
  columnHelper.accessor((row) => row.username, {
    username: "username",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Username" />
    ),
    id: "username",
  }),
  columnHelper.accessor((row) => row?.first_name, {
    first_name: "first_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="First Name" />
    ),
    cell: (props) => {
      const splitName = props?.row?.original?.first_name?.split(" ")
      return (
        <span className="flex flex-col">
          {splitName?.map((sso, i) => (
            <span key={i}>{sso}</span>
          ))}
        </span>
      )
    },
    id: "first_name",
  }),
  columnHelper.accessor((row) => row.last_name, {
    last_name: "last_name",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Last Name" />
    ),
    id: "last_name",
  }),
  columnHelper.accessor((row) => row.email, {
    email: "email",
    header: ({ column }) => <BasicSortHeader column={column} label="Email" />,
    id: "email",
  }),
  columnHelper.accessor((row) => row.test_user, {
    test_user: "test_user",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Test User" />
    ),
    id: "test_user",
  }),

  columnHelper.accessor((row) => row.courses_completed, {
    courses_completed: "courses_completed",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Completed Courses" />
    ),
    cell: (props) => {
      return <span>{props.row.original.courses_completed}</span>
    },
    id: "courses_completed",
  }),
  columnHelper.accessor((row) => row.progress, {
    in_progress_courses: "progress",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Progress" />
    ),
    id: "progress",
  }),
  columnHelper.accessor((row) => row.has_badge, {
    has_badge: "has_badge",
    header: ({ column }) => (
      <BasicSortHeader column={column} label="Has Badge" />
    ),
    id: "has_badge",
    cell: (props) => {
      return (
        <Typography variant="subtitle1">
          {props.row.original.has_badge === 1 ? "Yes" : "No"}
        </Typography>
      )
    },
  }),
]

const Analytics = ({ routes = [], ...rest }) => {
  const classes = useStyles()

  usePermissionsRedirect("/certifications", "analytics")

  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [searchValue, setSearchValue] = useState("")

  //sorting to go here in future
  let dataFetch = {
    searchValue: searchValue.length === 0 ? null : searchValue,
    limit: pageSize,
  }

  const { analyticsMutation, downloadAnalyticsMutation, certification } =
    useContext(CertificationContext)

  const { data, fetchNextPage, isFetching, refetch, hasNextPage } =
    useInfiniteQuery({
      queryKey: ["users", dataFetch],
      queryFn: async ({ pageParam = 0 }) => {
        return await certificationAnalytics(certification?.id, {
          ...dataFetch,
          offset: pageParam,
        })
      },
      getNextPageParam: (lastPage, allPages) => {
        const nextPageData = lastPage.nextOffset
        return nextPageData
      },
    })

  useEffect(() => {
    refetch()
  }, [certification?.id])

  return (
    <Page>
      <Box className={classes.container}>
        {/* <AnalyticsFilters type="path" /> */}
        <AnalyticsTable
          columns={columns}
          data={data}
          mutation={analyticsMutation}
          downloadMutation={downloadAnalyticsMutation}
          type="Certification"
          contentData={certification}
          initialSortString={"user_id"}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          refetch={refetch}
          hasNextPage={hasNextPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          setSearchValue={setSearchValue}
          searchValue={searchValue}
        />

        <Router routes={routes} {...rest} />
      </Box>
    </Page>
  )
}

Analytics.propTypes = {
  routes: PropTypes.array,
  setSearchValue: PropTypes.func,
  searchValue: PropTypes.string,
}

export default Analytics
